import { useParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { SearchFormProps, snackbar, useAppLayoutContextConsumer } from '@occc-app/design-system';
import {
  Cpg,
  ScanDataSftpCredentialFragment,
  SubmissionGroupLocationFragment,
} from '@occc/graphql';
import { LoadedData } from '@occc/utils';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';

import { buildSubmissionGroupDetailsBreadcrumbs } from './breadcrumbs';

import { routeCreator } from '../../scan-data.router';
import { useGetScanDataSftpCredentialByUuid } from '../../hooks/use-get-scan-data-sftp-credential-by-uuid.hook';
import { useLocationBulkSubmitterIdUpdate } from '../../hooks/use-location-bulk-submitter-id-update.mutation.hook';
import { useGetSubmissionGroupLocations } from '../../hooks/use-get-submission-group-locations.hook';

type SubmissionGroupsDetailsPageURLParams = {
  id: string;
};

export const useScanDataSubmissionGroupsDetails = () => {
  const urlParams = useParams<SubmissionGroupsDetailsPageURLParams>();
  const id = urlParams.id as string;

  const [isAddLocationsDialogOpen, setIsAddLocationsDialogOpen] = useState(false);
  const [isRemoveLocationsDialogOpen, setIsRemoveLocationsDialogOpen] = useState(false);
  const [isRemoveMode, setIsRemoveMode] = useState(false);

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();

  const [updateSubmitterIdForLocations, { loading: isUpdatingLocations }] =
    useLocationBulkSubmitterIdUpdate();

  const {
    data: sftpCredentialData,
    loading: sftpLoading,
    error: sftpError,
  } = useGetScanDataSftpCredentialByUuid(id);

  const sftLoadedData: LoadedData<ScanDataSftpCredentialFragment | null> = useMemo(() => {
    return {
      data: sftpCredentialData?.getScanDataSftpCredentialByUuid,
      loading: sftpLoading,
      error: sftpError,
    };
  }, [sftpCredentialData, sftpLoading, sftpError]);

  const sftpCredential = sftpCredentialData?.getScanDataSftpCredentialByUuid;

  const locationFilter = useMemo(
    () => ({
      altriaSubmitterIDEquals:
        sftpCredential?.cpg === Cpg.Altria ? sftpCredential?.submitterID : undefined,
      circanaSubmitterIDEquals:
        sftpCredential?.cpg === Cpg.Circana ? sftpCredential?.submitterID : undefined,
      msaSubmitterIDEquals:
        sftpCredential?.cpg === Cpg.Msa ? sftpCredential?.submitterID : undefined,
    }),
    [sftpCredential],
  );

  const {
    data,
    loading: locationsLoading,
    error: locationsError,
    paginationProps,
  } = useGetSubmissionGroupLocations({
    paginationInfo,
    onPageChange,
    search,
    baseOptions: {
      fetchPolicy: 'network-only',
      skip: !sftpCredential,
      variables: {
        filter: locationFilter,
      },
    },
  });

  const handleAddLocationsToGroup = async (bulkSelectedItems: Set<string>) => {
    if (bulkSelectedItems.size === 0 || !sftpCredential) {
      return;
    }

    const selectedLocationUuids = Array.from(bulkSelectedItems);

    const submitterID = sftpCredential.submitterID;
    const cpg = sftpCredential.cpg;

    const updateResult = await updateSubmitterIdForLocations({
      awaitRefetchQueries: true,
      refetchQueries: ['getSubmissionGroupLocations'],
      variables: {
        input: {
          uuids: selectedLocationUuids,
          altriaSubmitterID: cpg === Cpg.Altria ? submitterID : undefined,
          circanaSubmitterID: cpg === Cpg.Circana ? submitterID : undefined,
          msaSubmitterID: cpg === Cpg.Msa ? submitterID : undefined,
        },
      },
    });

    if (updateResult.errors) {
      snackbar.error('Unable to add Locations to Submission Group.');
      return;
    }

    setSearch('');
    setIsAddLocationsDialogOpen(false);

    snackbar.success('Locations were successfully added to Submission Group.');
  };

  const handleRemoveLocationsFromGroup = async (bulkSelectedItems: Set<string>) => {
    if (bulkSelectedItems.size === 0 || !sftpCredential) {
      return;
    }

    const selectedLocationUuids = Array.from(bulkSelectedItems);
    const cpg = sftpCredential.cpg;

    const updateResult = await updateSubmitterIdForLocations({
      awaitRefetchQueries: true,
      refetchQueries: ['getSubmissionGroupLocations'],
      variables: {
        input: {
          uuids: selectedLocationUuids,
          altriaSubmitterID: cpg === Cpg.Altria ? null : undefined,
          circanaSubmitterID: cpg === Cpg.Circana ? null : undefined,
          msaSubmitterID: cpg === Cpg.Msa ? null : undefined,
        },
      },
    });

    if (updateResult.errors) {
      snackbar.error('Unable to remove Locations from Submission Group.');
      return;
    }

    setSearch('');
    setIsRemoveLocationsDialogOpen(false);
    handleBulkDeselectAll();
    setIsRemoveMode(false);

    snackbar.success('Locations were successfully removed from Submission Group.');
  };

  const locationsLoadedData: LoadedData<SubmissionGroupLocationFragment[]> = useMemo(() => {
    return {
      data,
      loading: locationsLoading,
      error: locationsError,
    };
  }, [data, locationsLoading, locationsError]);

  const handleOnSearchSubmit = useCallback<SearchFormProps['onSubmit']>(
    ({ search: newSearchValue }) => setSearch(newSearchValue),
    [setSearch],
  );

  const title = sftpCredential?.name || 'Loading...';
  const subtitle = sftpCredential?.cpg || '';

  const breadcrumbs = useMemo(() => buildSubmissionGroupDetailsBreadcrumbs(title), [title]);
  useAppLayoutContextConsumer(breadcrumbs);

  return {
    id,
    title,
    subtitle,
    sftpCredential,
    search,
    handleOnSearchSubmit,
    paginationProps,
    sftLoadedData,
    locationsLoadedData,
    routeCreator,
    isAddLocationsDialogOpen,
    setIsAddLocationsDialogOpen,
    isRemoveLocationsDialogOpen,
    setIsRemoveLocationsDialogOpen,
    handleAddLocationsToGroup,
    handleRemoveLocationsFromGroup,
    isRemoveMode,
    setIsRemoveMode,
    bulkSelectedItems,
    handleBulkSelectMultiple,
    handleBulkDeselectAll,
    handleItemSelect,
    isUpdatingLocations,
  };
};
